import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, ShieldX, Search, Loader2, FileText, Calendar,
  User, CheckCircle, XCircle, AlertTriangle, Info, Eye, Clock
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const DocumentVerificationPage = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const verifyDocument = async () => {
    if (!verificationCode.trim()) {
      toast.error('Ingresa un código de verificación');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/documents/verify/${verificationCode}`);
      setResult(data);
      if (data.is_valid) {
        toast.success('Documento verificado exitosamente');
      } else {
        toast.error(data.error || 'Documento no válido');
      }
    } catch (error) {
      setResult({
        is_valid: false,
        error: 'Código no encontrado',
        message: 'El código de verificación ingresado no existe en nuestro sistema'
      });
      toast.error('Código de verificación no encontrado');
    }
    setLoading(false);
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      litigation: 'Análisis de Litigios',
      identity: 'Verificación de Identidad',
      contract: 'Contrato Legal',
      analysis: 'Análisis Legal'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-emerald-500/20 rounded-2xl">
              <ShieldCheck className="w-12 h-12 text-emerald-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Verificación de Documentos
          </h1>
          <p className="text-slate-400">
            Verifique la autenticidad de documentos generados por NotaryOS
          </p>
        </motion.div>

        {/* Search Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700 mb-8"
        >
          <label className="text-sm text-slate-400 block mb-2">
            Código de Verificación
          </label>
          <div className="flex space-x-3">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
              onKeyPress={(e) => e.key === 'Enter' && verifyDocument()}
              placeholder="Ej: LIT-20241215120000-A1B2-C3D4"
              className="flex-1 px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-emerald-500"
            />
            <button
              onClick={verifyDocument}
              disabled={loading || !verificationCode.trim()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              <span>Verificar</span>
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            El código se encuentra en la parte inferior del documento PDF
          </p>
        </motion.div>

        {/* Result */}
        <AnimatePresence mode="wait">
          {searched && result && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {result.is_valid ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl overflow-hidden">
                  {/* Success Header */}
                  <div className="bg-emerald-500/20 p-6 text-center">
                    <div className="flex justify-center mb-3">
                      <div className="p-3 bg-emerald-500/30 rounded-full">
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-emerald-400 mb-1">
                      Documento Verificado
                    </h2>
                    <p className="text-emerald-300/80 text-sm">
                      Este documento es auténtico y fue emitido por NotaryOS
                    </p>
                  </div>

                  {/* Document Details */}
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-400">Tipo de Documento</span>
                        </div>
                        <p className="text-white font-medium">
                          {getDocumentTypeLabel(result.document_type)}
                        </p>
                      </div>

                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-400">Fecha de Emisión</span>
                        </div>
                        <p className="text-white font-medium">
                          {formatDate(result.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-slate-800/50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ShieldCheck className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">Código de Verificación</span>
                      </div>
                      <p className="text-emerald-400 font-mono font-bold">
                        {result.verification_code}
                      </p>
                    </div>

                    {result.content_summary && (
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Info className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-400">Resumen</span>
                        </div>
                        <p className="text-slate-300">
                          {result.content_summary}
                        </p>
                      </div>
                    )}

                    <div className="bg-slate-800/50 rounded-xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Eye className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">Verificaciones Realizadas</span>
                      </div>
                      <p className="text-white font-medium">
                        {result.access_count} {result.access_count === 1 ? 'vez' : 'veces'}
                      </p>
                    </div>

                    <div className="flex items-center justify-center pt-4 border-t border-slate-700">
                      <div className="flex items-center space-x-2 text-emerald-400">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="font-medium">Plataforma: {result.platform}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl overflow-hidden">
                  {/* Error Header */}
                  <div className="bg-red-500/20 p-6 text-center">
                    <div className="flex justify-center mb-3">
                      <div className="p-3 bg-red-500/30 rounded-full">
                        <ShieldX className="w-10 h-10 text-red-400" />
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-red-400 mb-1">
                      {result.error || 'Documento No Verificado'}
                    </h2>
                    <p className="text-red-300/80 text-sm">
                      {result.message || 'Este documento no pudo ser verificado'}
                    </p>
                  </div>

                  {/* Warning Info */}
                  <div className="p-6">
                    <div className="bg-slate-800/50 rounded-xl p-4 flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-yellow-400 font-medium mb-1">Precaución</p>
                        <p className="text-slate-400 text-sm">
                          Si recibió este documento de un tercero, solicite una copia 
                          directamente desde la plataforma NotaryOS o verifique el código 
                          con el emisor original.
                        </p>
                      </div>
                    </div>

                    {result.invalidated_at && (
                      <div className="mt-4 bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-400">Fecha de Invalidación</span>
                        </div>
                        <p className="text-red-400 font-medium">
                          {formatDate(result.invalidated_at)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info Section */}
        {!searched && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700/50"
          >
            <h3 className="text-white font-medium mb-4 flex items-center space-x-2">
              <Info className="w-5 h-5 text-blue-400" />
              <span>¿Cómo funciona?</span>
            </h3>
            <div className="space-y-3 text-slate-400 text-sm">
              <p>
                <span className="text-emerald-400 font-medium">1.</span> Cada documento generado 
                por NotaryOS incluye un código de verificación único en la parte inferior.
              </p>
              <p>
                <span className="text-emerald-400 font-medium">2.</span> Este código está 
                criptográficamente vinculado al contenido del documento.
              </p>
              <p>
                <span className="text-emerald-400 font-medium">3.</span> Cualquier modificación 
                al documento invalidará el código de verificación.
              </p>
              <p>
                <span className="text-emerald-400 font-medium">4.</span> Solo los documentos 
                generados oficialmente por NotaryOS pueden ser verificados.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DocumentVerificationPage;
